import ImageSlider from "~/components/product/ProductDetails/ImageSlider"
import InfosAndBuyButton, {
    type Props as InfosProps,
} from "~/components/product/ProductDetails/InfosAndBuyButton"

import Breadcrumb from "~/components/ui/Breadcrumb"
import { sanitizeShortDescription } from "~/sdk/sanitizeProductHtml"
import { ProductDetailsPage } from "@decocms/apps-commerce/types"

import { TablesConfig } from "~/loaders/tabelaDeMedidas"
import { useTabelaDeMedidas } from "~/sdk/useTabelaDeMedidas"
import { type Section as Section } from "~/types/deco"
import { RenderSection } from "@decocms/blocks/hooks"
import { resolveDeferred } from "@decocms/blocks/cms"
import { type FnContext as FnContext } from "~/types/deco"
import { checkDisabledBuyMonthlySubscription } from "~/sdk/useSubscription"
import { SITE } from "~/constants"

export interface Props {
    /**
     * @title Integração
     */
    page: ProductDetailsPage | null
    /**
     * @title Seção de produto não encontrado
     * @description Seção que substituirá a "SearchResult" caso nenhum produto seja encontrado
     */
    notFoundSections: Section[]
    /**
     * @title Informações do Produto
     */
    productInfo: Omit<
        InfosProps,
        | 'product'
        | 'isMobile'
        | 'tabelaDeMedidas'
        | 'productName'
        | 'brand'
        | 'isPartial'
        | 'skuIdUrl'
        | 'disabledBuyMonthlySubscription'
        | 'model'
        | 'sku'
    >
    /**
     * @title Tabela de medidas
     */
    tabelaDeMedidas: TablesConfig[]
}

export async function loader(
    props: Props & { __pageUrl?: string; isMobile?: boolean },
) {
    const url = new URL(props.__pageUrl ?? "/", SITE.URL)

    // SKU escolhida na URL. No Fresh o clique de variante era uma partial nav e
    // o param era `idsku`; aqui virou navegação SPA e o param é `skuId`
    // (VariantLink, DECO-5445) — `idsku` fica só por compat com links antigos.
    // É este valor que diz ao buy box para usar a SKU resolvida pelo loader VTEX
    // em vez da variante mais barata (ver InfosAndBuyButton).
    const skuIdUrl = url.searchParams.get('skuId') ?? url.searchParams.get('idsku') ?? ''

    const disabledBuyMonthlySubscription = await checkDisabledBuyMonthlySubscription(
        props?.page?.product ? [props.page.product] : [],
    )

    // `notFoundSections` chega como THUNK (asResolved(.., true) no
    // onBeforeResolveProps): o resolver do CMS não caminha nela, então a vitrine
    // de fallback só é resolvida no caminho de 404.
    if (!props.page) {
        return {
            ...props,
            notFoundSections: (await resolveDeferred(
                props.notFoundSections,
            )) as Section[],
            isMobile: props.isMobile ?? false,
            isPartial: url.searchParams.get('fresh-partial') === 'true',
            skuIdUrl,
            disabledBuyMonthlySubscription,
        }
    }

    return {
        ...props,
        // Nunca renderizadas quando o produto existe, e a string serializada
        // não deve vazar para o payload de hidratação.
        notFoundSections: [] as Section[],
        isMobile: props.isMobile ?? false,
        isPartial: url.searchParams.get('fresh-partial') === 'true',
        skuIdUrl,
        disabledBuyMonthlySubscription,
        // Sanitizado aqui (servidor) para o `sanitize-html` ficar fora do bundle.
        shortDescription: await sanitizeShortDescription(
            props.page.product.description || props.page.product.isVariantOf?.description,
        ),
    }
}
export default function ProductDetails(
    {
        page,
        notFoundSections,
        productInfo,
        isMobile,
        tabelaDeMedidas,
        isPartial,
        skuIdUrl,
        disabledBuyMonthlySubscription,
        ...rest
    }: Awaited<ReturnType<typeof loader>>,
) {
    // Só o ramo com produto devolve o resumo (o de 404 não renderiza o buy box).
    const shortDescription = 'shortDescription' in rest ? rest.shortDescription : ''
    if (!page) {
        return (
            <div>
                {notFoundSections.map((section, i) => (
                    <RenderSection key={i} section={section as never} />
                ))}
            </div>
        )
    }
    const { breadcrumbList, product } = page
    const productName = product.isVariantOf?.name ?? product.name ?? 'Produto'
    const brand = page.product.brand?.name
    const addProperties = product.isVariantOf?.additionalProperty ?? []
    const tableTitle = addProperties.find(({ name }) => name?.toLowerCase() === 'titulos')?.value
    const tableRow1 = addProperties.find(({ name }) => name?.toLowerCase() === 'linha 1')?.value
    const tableRow2 = addProperties.find(({ name }) => name?.toLowerCase() === 'linha 2')?.value
    const tableRow3 = addProperties.find(({ name }) => name?.toLowerCase() === 'linha 3')?.value
    const tableRow4 = addProperties.find(({ name }) => name?.toLowerCase() === 'linha 4')?.value
    const tableRow5 = addProperties.find(({ name }) => name?.toLowerCase() === 'linha 5')?.value
    const tableRow6 = addProperties.find(({ name }) => name?.toLowerCase() === 'linha 6')?.value
    let table = ''
    if (tableTitle) {
        const header = tableTitle.split('??')
        const row1 = tableRow1?.split('??')
        const row2 = tableRow2?.split('??')
        const row3 = tableRow3?.split('??')
        const row4 = tableRow4?.split('??')
        const row5 = tableRow5?.split('??')
        const row6 = tableRow6?.split('??')
        const rows = [row1, row2, row3, row4, row5, row6]
        table = `
        <table>
            <thead>
                <tr>
                    ${header.map((h) => `<th>${h}</th>`).join('')}
                </tr>
            </thead>
            <tbody>
                ${
            rows
                .map((row) => {
                    if (!row) {
                        return ''
                    }
                    return `
                            <tr>
                                ${row.map((r) => `<td>${r}</td>`).join('')}
                            </tr>
                        `
                })
                .join('')
        }
            </tbody>
        </table>
        `
    } else {
        table = useTabelaDeMedidas({
            tables: tabelaDeMedidas,
            product,
        })?.table ?? ''
    }
    product.image ??= []
    const images = [...product.image].sort((a, b) => {
        if (a.name === 'Principal') {
            return -1
        }
        if (b.name === 'Principal') {
            return 1
        }
        return 0
    })
    return (
        <div id='product-page' className='mb-12'>
            <div className='px-4 pt-3 pb-1 mx-auto max-w-[1770px]'>
                <div className='pb-1 overflow-x-auto scroll scroll-neutral'>
                    <Breadcrumb itemListElement={breadcrumbList.itemListElement} />
                </div>
            </div>

            <div className='grid grid-cols-1 lg:grid-cols-[1fr_40%] gap-16 px-4 lg:px-6 pt-8 max-w-[1770px] mx-auto'>
                <ImageSlider
                    // Remount per SKU: SliderJS binds listeners/observers to the
                    // DOM nodes present on mount, so swapping variant images in
                    // place would leave dead controls and a stale scroll offset.
                    key={product.sku}
                    isMobile={isMobile}
                    productURL={product.url}
                    width={450}
                    height={450}
                    images={images}
                    productName={productName}
                />
                <InfosAndBuyButton
                    {...productInfo}
                    product={product}
                    skuIdUrl={skuIdUrl}
                    tabelaDeMedidas={table}
                    isMobile={isMobile}
                    isPartial={isPartial}
                    disabledBuyMonthlySubscription={disabledBuyMonthlySubscription}
                    productName={productName}
                    brand={brand}
                    model={product.isVariantOf?.model ?? product.productID}
                    sku={product.sku}
                    shortDescription={shortDescription}
                />
            </div>
        </div>
    )
}
export function LoadingFallback() {
    return (
        <div>
            <div className='h-9 animate-pulse p-2'>
                <div className='ml-4 h-2.5 bg-gray-200 rounded-full w-48'></div>
            </div>

            <div className='animate-pulse h-screen grid grid-cols-1 lg:grid-cols-[1fr_40%] gap-16 px-4 lg:px-6 pt-8 max-w-[1770px] mx-auto'>
                <div className='h-full grid grid-cols-1 grid-rows-[1fr_auto] select-none sm:grid-cols-[auto_1fr] sm:grid-rows-1 lg:self-start lg:sticky lg:top-4 lg:!z-10'>
                    <div className='lg:w-24 lg:h-full flex lg:flex-col gap-4 order-2 carousel carousel-center sm:flex-col sm:order-1'>
                        <div className='bg-gray-200 rounded-lg w-20 h-20'></div>
                        <div className='bg-gray-200 rounded-lg w-20 h-20'></div>
                        <div className='bg-gray-200 rounded-lg w-20 h-20'></div>
                    </div>
                    <div className='bg-gray-200 relative order-1 sm:order-2 min-w-96 aspect-square rounded-lg'>
                    </div>
                </div>

                <div className='min-w-96 h-full bg-gray-200 rounded-lg'>
                </div>
            </div>
        </div>
    )
}
